import ROUTES from "../router/routesModel";

/**
 * Auth redirect handling for the packaged Android app. See #83.
 *
 * THE PROBLEM
 * -----------
 * `capacitor.config.ts` sets `androidScheme: 'https'`, so inside the WebView
 * `window.location.origin` is `https://localhost`. Every auth flow derived its
 * redirect from that origin, so Supabase was told to send the user back to
 * `https://localhost/auth/callback` — an address that, in the external browser
 * where the flow actually finishes, is not this app and is not anything at all.
 * Nothing could hand the result back, because no intent filter claimed any URL.
 *
 * THE FIX
 * -------
 * On native, redirect to a custom scheme this app *does* claim
 * (`AndroidManifest.xml` — VIEW/BROWSABLE intent filter). Android hands the URL
 * to the running app, `useAuthDeepLink` turns it back into an in-app route, and
 * the existing AuthCallbackPage completes the exchange exactly as on web.
 *
 * WHY A CUSTOM SCHEME AND NOT VERIFIED APP LINKS
 * ----------------------------------------------
 * Verified `https://` App Links need the signing certificate's SHA-256 in a
 * published assetlinks.json. Play App Signing re-signs the bundle with a
 * different key than the one uploaded, so that fingerprint has to be kept in
 * sync or sign-in breaks *only* for store installs — the failure that doesn't
 * show up on the developer's own device. A custom scheme has no fingerprint to
 * drift. App Links remain the better long-term answer for link-hijack
 * resistance; this is the one that cannot silently break the beta.
 *
 * Nothing here imports @capacitor/* at module scope: Capacitor injects
 * `window.Capacitor` in native builds, so detection is a plain global read and
 * this module stays trivially testable in jsdom.
 */

/** Must match `applicationId` in android/app/build.gradle and the manifest. */
export const NATIVE_AUTH_SCHEME = "com.cardonomer.app";

const SCHEME_PREFIX = `${NATIVE_AUTH_SCHEME}://`;

/** True only inside the packaged native shell; false on web and in tests. */
export const isNativePlatform = () => {
  try {
    return Boolean(window?.Capacitor?.isNativePlatform?.());
  } catch {
    return false;
  }
};

/**
 * Where Supabase should send the user back to.
 *
 * Native gets the claimed custom scheme; web keeps the existing behaviour
 * exactly, so nothing about the browser flow changes.
 *
 * Both values must be listed in Supabase → Authentication → URL Configuration →
 * Redirect URLs, or Supabase rejects the redirect before Google is ever reached.
 */
export const buildAuthRedirectUrl = (pathname = ROUTES.AUTH_CALLBACK) => {
  const path = String(pathname || ROUTES.AUTH_CALLBACK);
  if (isNativePlatform()) {
    return `${SCHEME_PREFIX}${path.replace(/^\/+/, "")}`;
  }
  return `${window.location.origin}${path}`;
};

/**
 * Convert an incoming deep link into the in-app route to navigate to, or null
 * if this link is not an auth callback we accept.
 *
 * Deliberately an allowlist of one route rather than "strip the scheme and
 * navigate wherever it says": any installed app can fire an intent at a custom
 * scheme, so a link must not be able to steer the app to an arbitrary screen.
 * The query and hash are preserved verbatim — that is where the PKCE `code`
 * and any recovery fragment live, and AuthCallbackPage reads them off
 * `window.location`.
 */
export const parseDeepLinkAuthTarget = (url) => {
  if (typeof url !== "string") return null;
  if (!url.toLowerCase().startsWith(SCHEME_PREFIX.toLowerCase())) return null;

  const remainder = url.slice(SCHEME_PREFIX.length);
  if (!remainder) return null;

  const separatorIndex = remainder.search(/[?#]/);
  const rawPath = separatorIndex === -1 ? remainder : remainder.slice(0, separatorIndex);
  const suffix = separatorIndex === -1 ? "" : remainder.slice(separatorIndex);

  // "auth/callback" and "auth/callback/" both mean the callback route.
  const normalisedPath = `/${rawPath}`.replace(/\/+$/, "") || "/";
  if (normalisedPath !== ROUTES.AUTH_CALLBACK) return null;

  return `${ROUTES.AUTH_CALLBACK}${suffix}`;
};

/**
 * Open the provider URL in a system Custom Tab instead of navigating the
 * WebView. Navigating the WebView away from the app origin tears down the
 * running React app — the user would come back to a cold boot with the PKCE
 * verifier still in storage but no app state.
 *
 * Imported lazily so the web bundle and the Jest environment never load the
 * native plugin.
 */
export const openExternalAuthUrl = async (url) => {
  const { Browser } = await import("@capacitor/browser");
  await Browser.open({ url });
};

/** Close the Custom Tab once the deep link has brought us back. */
export const closeExternalAuthBrowser = async () => {
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch {
    // Already dismissed by the user, or no browser open — not worth surfacing.
  }
};
